import React from 'react';

import type ReserveInfo from '@this/domain/reserve_info2';
import { Text as AITText } from '@this/components/shared/ui/data_displays/typography';
import { useShortDistance, SHORT_DISTANCE_MODE } from '@this/src/lib/hooks/useShortDistance';
import { digits } from '@this/src/util';
import type Service from '@this/domain/service';
import Tooltip from '@this/shared/tooltip/tooltip';
import BufferAmountsSection from '@this/components/reserve_trip/reserve_confirm/outline/buffer_amounts_section';

import ReservingTrip from '@this/domain/trip/reserving_trip2';

import Text from '../../../shared/text/text';
import ReserveConfirmOutlineItemTemplate from '../outline_item/outline_item.template';
import ReserveConfirmOutlineItemShortdistanceTemplate from '../outline_item/outline_item_shortdistance.template';

import ReserveConfirmOutlineDetailTemplate from '../outline_detail/outline_detail.template';
import { formatDateRangeWithWeekday } from '@this/src/util/date';

type Props = {
  service: Service;
  reserveInfo: ReserveInfo;
};

const ReserveConfirmOutlineTemplate: React.FC<Props> = ({ service, reserveInfo }) => {
  const { shortDistanceMode } = useShortDistance();
  const styles = getStyles();

  return (
    <div className="reserve-confirm__outline" id="right-navi-content" data-testid="reserve-confirm-summary-root">
      <div style={styles.rightSubTitle} data-testid="reserve-confirm-summary-title">
        予約内容の概要
      </div>
      <div className="reserve-confirm-right-wrapper">
        <div style={styles.rightSection}>
          <AITText color="danger">
            下記が最終的なご依頼内容となります。日時や金額等、間違いがないかご確認ください。
          </AITText>
        </div>
        <div style={styles.rightSection} data-testid="reserve-confirm-summary-section-schedule">
          <div className="reserve-confirm-right-title" data-testid="reserve-confirm-summary-schedule-title">
            日程
          </div>
          <div className="reserve-confirm-right-content" data-testid="reserve-confirm-summary-schedule-content">
            <p data-wovn-ignore>
              {formatDateRangeWithWeekday(reserveInfo.reservingTrip.startDate, reserveInfo.reservingTrip.endDate)}
            </p>
          </div>
        </div>
        {reserveInfo.reservingTrip.items && reserveInfo.reservingTrip.items.length > 0 && (
          <>
            <div style={styles.rightSection} data-testid="reserve-confirm-summary-section-total-price">
              <div style={styles.totalPriceBox} data-testid="reserve-confirm-summary-total-price-box">
                当サイトで予約可能な合計金額
                <div
                  style={styles.totalPriceBoxPrice}
                  data-wovn-ignore
                  data-testid="reserve-confirm-summary-total-price"
                >
                  {digits(reserveInfo.reservingTrip.totalPrice)}円
                </div>
              </div>
              {reserveInfo.reservingTrip.itemsTotalBufferAmount > 0 && (
                <div style={styles.totalPriceBox} data-testid="reserve-confirm-summary-buffer-price-box">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px' }}>値上がり許容幅込の申請金額</span>
                    <Tooltip type="info">
                      <div className="select__tooltip-format">
                        承認後、実際に商品を手配する前に値上がりが発生した場合、いくらまで値上がりを許容して手配するかを管理画面で設定いただいています。
                      </div>
                    </Tooltip>
                  </div>
                  <div>{digits(reserveInfo.reservingTrip.totalPriceWithBuffer)}円</div>
                </div>
              )}
            </div>
            {reserveInfo.reservingTrip.hasLegSummaries && (
              <div style={styles.rightSection}>
                <div className="reserve-confirm-right-title">【この旅程にかかる金額詳細】</div>
                <div className="reserve-confirm-right-content-note">
                  ※AIトラベル以外の提示している金額はあくまで目安です。
                </div>
              </div>
            )}
            {reserveInfo.reservingTrip.itemsTotalBufferAmount > 0 && (
              <BufferAmountsSection reservingTrip={reserveInfo.reservingTrip} />
            )}
            <div style={styles.outlineBox} data-testid="reserve-confirm-summary-section-item-details">
              <div style={styles.outlineBoxTitle} data-testid="reserve-confirm-summary-item-details-title">
                当サイトで予約可能な商品の詳細
              </div>
              {reserveInfo.reservingTrip.items.map(
                (item, i) =>
                  item && (
                    <ReserveConfirmOutlineItemTemplate
                      key={i}
                      isPackage={reserveInfo.reservingTrip.isPackage}
                      domesticAirPriceIndex={item.domesticAirPriceIndex}
                      item={item}
                    />
                  )
              )}
              <hr style={styles.outlineBoxHr} />
              <div style={styles.rightContentTotal} data-testid="reserve-confirm-summary-item-details-footer">
                {reserveInfo.reservingTrip.isPackage ? (
                  <div
                    style={styles.totalPrice}
                    data-wovn-ignore
                    data-testid="reserve-confirm-summary-item-details-package-breakdown"
                  >
                    <Text data-wovn-ignore text={reserveInfo.reservingTrip.tooltipText} />
                  </div>
                ) : (
                  <div style={styles.totalPrice} data-wovn-ignore>
                    {digits(reserveInfo.reservingTrip.totalPrice)}円(税込)
                  </div>
                )}
              </div>
            </div>
            {reserveInfo.reservingTrip.hasLegSummaries && shortDistanceMode === SHORT_DISTANCE_MODE.ALL && (
              <>
                <div style={styles.outlineBox}>
                  <div style={styles.outlineBoxTitle}>その他経費目安</div>
                  {reserveInfo.reservingTrip.items.map(
                    item =>
                      item &&
                      item.element && (
                        <ReserveConfirmOutlineItemShortdistanceTemplate
                          element={item.element}
                          elementType={item.elementType}
                        />
                      )
                  )}
                  <hr style={styles.outlineBoxHr} />
                  <div style={styles.rightContentTotal}>
                    <div style={styles.totalPrice} data-wovn-ignore>
                      {digits(reserveInfo.reservingTrip.totalPriceShortDistance)}円(税込)
                    </div>
                  </div>
                </div>
                <div style={styles.outlineBoxAllTotal}>
                  <div style={styles.outlineBoxTitleAllTotal}>この旅程でかかる費用目安</div>
                  <div style={styles.totalPriceAllTotal} data-wovn-ignore>
                    {digits(
                      reserveInfo.reservingTrip.totalPrice + reserveInfo.reservingTrip.totalPriceShortDistance
                    )}
                    円(税込)
                  </div>
                </div>
              </>
            )}
            {reserveInfo.reservingTrip.isAirPackage && (
              <div style={styles.rightSection} data-testid="reserve-confirm-summary-section-package-company">
                <div style={styles.rightTitle} data-testid="reserve-confirm-summary-package-organizer-title">
                  ■旅行企画・実施
                </div>
                <div style={styles.rightContent} data-testid="reserve-confirm-summary-package-organizer-content">
                  <Text
                    text={
                      reserveInfo.reservingTrip.isAnaAirPackage
                        ? ReservingTrip.anaPackageDesc()
                        : reserveInfo.reservingTrip.isJalAirPackage
                        ? ReservingTrip.jalPackageDesc()
                        : ''
                    }
                  />
                </div>
                <div style={styles.rightTitle} data-testid="reserve-confirm-summary-package-consignment-title">
                  ■受託販売会社
                </div>
                <div style={styles.rightContent} data-testid="reserve-confirm-summary-package-consignment-content">
                  <Text text={ReservingTrip.consignmentSaleDesc(service?.consignmentSaleTel)} />
                </div>
              </div>
            )}
            <br />
            <div style={styles.rightSection}>
              <div className="reserve-confirm-right-title">経路詳細</div>
              <ReserveConfirmOutlineDetailTemplate reservingTrip={reserveInfo.reservingTrip} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function getStyles() {
  return {
    rightSubTitle: {
      fontSize: '18px',
      fontWeight: 600,
      marginBottom: '10px'
    } as React.CSSProperties,
    rightSection: {
      marginBottom: '15px'
    } as React.CSSProperties,
    rightTitle: {
      fontWeight: 'bold'
    } as React.CSSProperties,
    rightContent: {
      marginLeft: '10px'
    } as React.CSSProperties,
    rightContentTotal: {
      textAlign: 'right',
      padding: '5px'
    } as React.CSSProperties,
    totalPrice: {
      fontSize: '13px',
      fontWeight: 'bold'
    } as React.CSSProperties,
    totalPriceBox: {
      backgroundColor: '#feffd2',
      padding: '10px',
      fontWeight: 'bold',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    } as React.CSSProperties,
    totalPriceBoxPrice: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#dc2626'
    } as React.CSSProperties,
    outlineBox: {
      border: '1px solid #d1d5db',
      marginTop: '-1px'
    } as React.CSSProperties,
    outlineBoxTitle: {
      padding: '10px',
      fontWeight: 'bold',
      backgroundColor: '#f9fafb'
    } as React.CSSProperties,
    outlineBoxHr: {
      margin: '4px 0'
    } as React.CSSProperties,
    outlineBoxAllTotal: {
      border: '1px solid #d1d5db',
      marginTop: '-1px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    } as React.CSSProperties,
    outlineBoxTitleAllTotal: {
      padding: '10px',
      fontWeight: 'bold',
      fontSize: '13px',
      backgroundColor: '#e5e7eb',
      width: '55%'
    } as React.CSSProperties,
    totalPriceAllTotal: {
      padding: '5px',
      fontSize: '16px',
      fontWeight: 'bold'
    } as React.CSSProperties
  } as const;
}

export default ReserveConfirmOutlineTemplate;
